import React from 'react';
import PropTypes from 'prop-types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * TextResponseList Component for displaying text responses
 * Handles short_text and long_text question types
 */
const TextResponseList = ({ responses, isExpanded, onToggleExpand }) => {

  if (!responses || responses.length === 0) {
    return (
      <div className="text-center py-4 text-muted-foreground">
        No text responses received
      </div>
    );
  }

  // Filter out empty responses
  const validResponses = responses.filter(response => 
    response && 
    typeof response === 'string' && 
    response.trim().length > 0
  );

  if (validResponses.length === 0) {
    return (
      <div className="text-center py-4 text-muted-foreground">
        No valid text responses
      </div>
    );
  }

  // Show first 3 by default, all when expanded
  const displayResponses = isExpanded ? validResponses : validResponses.slice(0, 3);
  const hasMore = validResponses.length > 3;



  return (
    <div className="w-full">


      {/* Response list - Masonry layout for varied text lengths */}
      <div className="columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4">
        {displayResponses.map((response, index) => (
          <div 
            key={index}
            className="p-3 bg-background border rounded-lg hover:bg-muted/50 transition-colors break-inside-avoid mb-4"
          >
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="text-xs flex-shrink-0">
                {index + 1}
              </Badge>
              <p className="text-base sm:text-sm leading-relaxed flex-1">
                {response}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Show more/less button */}
      {hasMore && (
        <div className="mt-4 text-center">
          <Button
            variant="ghost"
            onClick={onToggleExpand}
            className="flex items-center gap-2"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-4 w-4" />
                Show Less ({displayResponses.length - 3} hidden)
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4" />
                Show All {validResponses.length - 3} More Responses
              </>
            )}
          </Button>
        </div>
      )}

    </div>
  );
};

TextResponseList.propTypes = {
  responses: PropTypes.arrayOf(PropTypes.string),
  isExpanded: PropTypes.bool,
  onToggleExpand: PropTypes.func,
};

TextResponseList.defaultProps = {
  responses: [],
  isExpanded: false,
  onToggleExpand: () => {},
};

export default TextResponseList;